/**
 * checkTeamUserPermission — Generic helper to determine a user's effective
 * role on a specific team.
 *
 * Two-layer resolution:
 *   Layer 1 (User-level): The user's global role from the `users` table
 *     (`users.attributes.role`).
 *   Layer 2 (Team-level): The user's role on the specific team, resolved
 *     SERVER-SIDE by the `getEffectiveTeamRole` query (design D2/D9 of
 *     .kiro/specs/finn-mcp-team-permissions).
 *
 * Effective role = MAX(user-level, team-level)
 * Role hierarchy: admin (3) > operator (2) > user (1)
 *
 * Team-level resolution (server-side):
 *   - The `getEffectiveTeamRole` resolver computes MAX(role) over the
 *     caller's direct `team_user_map` row and the Permissions-group rows the
 *     caller is a BRASS-VERIFIED member of (`ctx.stash.permissions_teams`,
 *     produced by the same `check_permissions_team` Lambda the team list
 *     already uses).
 *   - Both group-row conventions (current: `users.user_name` holds the
 *     human-readable team name and `attributes.group_id` the
 *     `amzn1.abacus.team.*` id; legacy: `users.user_name` IS the
 *     `amzn1.abacus.team.*` id) are recognised server-side by
 *     `_GROUP_ROWS_SQL` in `check_permissions_team.py`. No convention
 *     predicate exists client-side any more.
 *   - Over-grant is impossible: group rows for groups the caller is NOT a
 *     verified member of never contribute. (The former client-side scan took
 *     the max role over EVERY group row on the team — risk R-5 — and has
 *     been deleted.)
 *   - Team-level role is null when the caller has neither a direct row nor a
 *     BRASS-verified group row on the team ("not a member").
 */

import { generateAuditedClient } from "../../audit/auditedClient";

// ─── Constants ──────────────────────────────────────────────────────────────

export const ROLE_ADMIN = "admin";
export const ROLE_OPERATOR = "operator";
export const ROLE_USER = "user";

const ROLE_HIERARCHY: Record<string, number> = {
  [ROLE_ADMIN]: 3,
  [ROLE_OPERATOR]: 2,
  [ROLE_USER]: 1,
};

// ─── Internal helpers ───────────────────────────────────────────────────────

function roleRank(role: string | null | undefined): number {
  if (!role) return 0;
  return ROLE_HIERARCHY[role.toLowerCase()] ?? 0;
}

function rankToRole(rank: number): string {
  if (rank >= 3) return ROLE_ADMIN;
  if (rank >= 2) return ROLE_OPERATOR;
  return ROLE_USER;
}

// ─── GraphQL query ──────────────────────────────────────────────────────────

const GET_EFFECTIVE_TEAM_ROLE = `
  query GetEffectiveTeamRole($input: GetEffectiveTeamRoleInput!) {
    getEffectiveTeamRole(input: $input) {
      team_id
      role
    }
  }
`;

// ─── Layer 1: User-level role ───────────────────────────────────────────────

/**
 * Get the user's global role from the users table.
 *
 * @param userAttributes - Parsed JSON from users.attributes (or raw string)
 * @returns The user's global role ("admin" | "operator" | "user")
 */
export function getUserLevelRole(userAttributes: any): string {
  if (!userAttributes) return ROLE_USER;
  const attrs =
    typeof userAttributes === "string"
      ? (() => {
          try {
            return JSON.parse(userAttributes);
          } catch {
            return {};
          }
        })()
      : userAttributes;
  return attrs.role || ROLE_USER;
}

// ─── Layer 2: Team-level role ───────────────────────────────────────────────

/**
 * Fetch the user's team-level role from the server-side
 * `getEffectiveTeamRole` query.
 *
 * The role is computed next to the only trustworthy group-membership signal
 * (the BRASS check inside the AppSync pipeline), so both group-row
 * conventions are handled and only groups the caller is a verified member of
 * contribute — see the module doc above.
 *
 * @param userName - The caller's alias (users.user_name)
 * @param teamId   - The target team ID
 * @returns The team-level role ("admin" | "operator" | "user"), or null when
 *          the caller has no direct row and no BRASS-verified group row on
 *          the team
 */
export async function fetchTeamLevelRole(
  userName: string,
  teamId: number
): Promise<string | null> {
  // Audited client so the getEffectiveTeamRole operation carries the
  // X-Audit-Context header, matching the audit behavior of the inline
  // MainPage.tsx role query this wrapper replaced (task 12.2).
  const client = generateAuditedClient();

  const result: any = await client.graphql({
    query: GET_EFFECTIVE_TEAM_ROLE,
    variables: { input: { user_name: userName, team_id: teamId } },
  });

  return result.data?.getEffectiveTeamRole?.role ?? null;
}

// ─── Combined: checkTeamUserPermission ──────────────────────────────────────

export interface TeamPermissionResult {
  /** The final effective role (MAX of user-level and team-level). */
  effectiveRole: string;
  /** The user's global role from the users table. */
  userLevelRole: string;
  /** The user's team-specific role (personal + group), or null if no entry. */
  teamLevelRole: string | null;
  /** Whether the user has admin or operator privileges. */
  canManage: boolean;
}

/**
 * Check a user's effective permission on a specific team.
 *
 * Resolves:
 *   - Layer 1: user-level global role (from userAttributes)
 *   - Layer 2: team-level role via the server-side `getEffectiveTeamRole`
 *     query (fetchTeamLevelRole)
 *   - Effective = MAX(layer 1, layer 2)
 *
 * @param userName        - The caller's alias (users.user_name)
 * @param teamId          - The target team ID to check
 * @param userAttributes  - The user's global attributes (parsed or raw JSON string)
 * @returns TeamPermissionResult with effective role and breakdown
 */
export async function checkTeamUserPermission(
  userName: string,
  teamId: number,
  userAttributes: any
): Promise<TeamPermissionResult> {
  // Layer 1: User-level
  const userRole = getUserLevelRole(userAttributes);

  // Layer 2: Team-level (server-side, BRASS-verified)
  const teamRole = await fetchTeamLevelRole(userName, teamId);

  // Effective = MAX(user-level, team-level)
  const effectiveRank = Math.max(roleRank(userRole), roleRank(teamRole));
  const effectiveRole = rankToRole(effectiveRank);

  return {
    effectiveRole,
    userLevelRole: userRole,
    teamLevelRole: teamRole,
    canManage: effectiveRole === ROLE_ADMIN || effectiveRole === ROLE_OPERATOR,
  };
}
